import { IconButton } from '@mui/material'
import { ContentCopy, GTranslate, VolumeDown } from '@mui/icons-material'
import CircularProgress from '@mui/material/CircularProgress'
import React, { useState, useMemo } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../hooks'
import { pronounce } from '../Store/PronounceSlice'
import { resetTranslate, translate, makeSelectTranslateState } from '../Store/TranslateSlice'
import { copyToClipboard, sanitizeCell } from '../Utilities/tools'

import './VocabularyTools.less'

export type DisplayTool = 'pronounce' | 'copy' | ''

interface VocabularyToolsProps {
  rowIndex: number
  rowInput?: { left: string, right: string }
  side: 'left' | 'right'
  vocabulary: { left_language: string, right_language: string, rows: Array<{ left: string, right: string }> }
  readOnly: boolean
  displayTools: string
  onTranslationResult: (translated: string) => void
}

const VocabularyTools = ({
                           rowIndex,
                           rowInput,
                           side,
                           vocabulary,
                           readOnly,
                           displayTools,
                           onTranslationResult,
                         }: VocabularyToolsProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const row = vocabulary.rows[rowIndex]
  const value = sanitizeCell(rowInput?.[side] || row[side] || '')
  const otherSide = side === 'left' ? 'right' : 'left'
  const otherValue = sanitizeCell(rowInput?.[otherSide] || row[otherSide] || '')
  const language = side === 'left' ? vocabulary.left_language : vocabulary.right_language
  const otherLanguage = side === 'left' ? vocabulary.right_language : vocabulary.left_language
  const translateKey = `row-${rowIndex}-${side}`

  // Create memoized selector for this specific translate key
  const selectTranslateState = useMemo(
    () => makeSelectTranslateState(translateKey),
    [translateKey]
  )
  const translateState = useAppSelector(selectTranslateState)
  const isTranslating = translateState.status === 'loading'

  const [lastPronounced, setLastPronounced] = useState('')
  const handlePronounce = async (text: string, language: string) => {
    const slow = (text === lastPronounced)
    const result = await dispatch(pronounce({ text, language, slow }))
    if (!pronounce.fulfilled.match(result)) {
      toast.error(t('PronunciationFailed'))
    }
    setLastPronounced((slow) ? '' : text)
  }

  const handleCopyToClipboard = (text: string) => {
    copyToClipboard(text).then(() => {
      toast.success(t('CopiedToClipboard'))
    })
  }

  const handleTranslate = async () => {
    dispatch(resetTranslate(translateKey))
    const result = await dispatch(translate({
      text: otherValue,
      source: otherLanguage,
      target: language,
      key: translateKey,
    }))
    if (translate.fulfilled.match(result)) {
      onTranslationResult(result.payload.data.result || '')
    } else {
      toast.error(t('TranslateFailed'))
    }
  }

  return (
    <div className="vocabulary-tools" tabIndex={-1}>
      {value && (
        <>
          {displayTools === 'pronounce' && (
            <IconButton
              tabIndex={-1}
              onClick={async () => {
                await handlePronounce(value, language)
              }}
            >
              <VolumeDown />
            </IconButton>
          )}
          {displayTools === 'copy' && (
            <IconButton
              tabIndex={-1}
              onClick={() => {
                handleCopyToClipboard(value)
              }}
            >
              <ContentCopy />
            </IconButton>
          )}
        </>
      )}
      {/* Translate button logic: show if otherValue is filled and value is empty (ignoring whitespace and HTML tags), regardless of displayTools */}
      {sanitizeCell(otherValue).trim() && !sanitizeCell(value).trim() && !readOnly && (
        <IconButton
          tabIndex={-1}
          onClick={handleTranslate}
          disabled={isTranslating}
        >
          {isTranslating ? <CircularProgress size={24} /> : <GTranslate />}
        </IconButton>
      )}
    </div>
  )
}

export default VocabularyTools